import { useMemo } from 'react';
import Table from '@antimatterhq/ui/components/table';
import { useTableColumns } from '@antimatterhq/ui/hooks/table/useTableColumns';
import { useTableColumnSorting } from '@antimatterhq/ui/hooks/table/useTableColumnSorting';
import {
  useVirtuosoTableRows,
  VirtualizedCustomBodyCells,
} from '@antimatterhq/ui/hooks/table/useVirtuosoTableRows';
import { FlagSet } from '@antimatterhq/ui/utils/flags';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { TableColumnDefinition } from '@antimatterhq/ui/types/TableColumnDefinition';
import {
  tableColumnDefinitionsWithFilteringDisabled,
  tableColumnDefinitionsWithTooltipDisabled,
} from '@antimatterhq/ui/utils/table/helpers';
import { Button } from '@antimatterhq/ui/components/button';
import { Icon } from '@antimatterhq/ui/components/icon';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Text } from '@antimatterhq/ui/components/typography';
import { useNavigate } from 'react-router-dom';
import { DataSourceAlertCount } from '../dataSourceCardAndTable/dataSourceAlertCount/DataSourceAlertCount';
import { DatasourceStatus } from '@/components/dataSources/dataSourceCardAndTable/datasourceStatus/DatasourceStatus';
import { DataSourceEnabledBadge } from '@/components/dataSources/dataSourceCardAndTable/dataSourceEnabledBadge/DataSourceEnabledBadge';
import { DataSourceEventCount } from '@/components/dataSources/dataSourceCardAndTable/dataSourceEventCount/DataSourceEventCount';
import { DataSourceIcon } from '@/components/dataSources/dataSourceCardAndTable/dataSourceIcon/DataSourceIcon';
import { DataSourceMenu } from '@/components/dataSources/dataSourceCardAndTable/dataSourceMenu/DataSourceMenu';
import { DataSourceSparkline } from '@/components/dataSources/dataSourceCardAndTable/dataSourceSparkline/DataSourceSparkline';
import { DataSourceUpgrade } from '@/components/dataSources/dataSourceCardAndTable/dataSourceUpgrade/DataSourceUpgrade';
import { DatasourceSummary } from '@/types/api';
import { useGetDatasourcesSummary } from '@/hooks/dbui/useGetDataSourcesSummary';

interface CurrentDataSourceTableData {
  id: string;
  name: string;
  description?: string;
  author?: string;
  group?: string;
  enabled: boolean;
  status: boolean;
  health: 'healthy' | 'error' | undefined;
  eventsLast24hrs?: number;
  eventsLast7days?: number;
  alertsLast7days?: number;
  iconURL?: string | null;
  sparklineData?: number[];
  originalDataSource: DatasourceSummary;
  isLoadingMetricsAndAlerts: boolean;
}

export const getCurrentDataSourcesTableColumnDefinitions = (
  showUpgradeColumn: boolean,
): TableColumnDefinition[] => {
  const baseColumns: TableColumnDefinition[] = [
    {
      id: 'name',
      title: 'Source',
      width: 300,
    },
    {
      id: 'image',
      title: '',
      width: 56,
      enableResize: false,
      enableSorting: false,
      enableFiltering: false,
    },
    {
      id: 'author',
      title: 'Author',
      width: 150,
    },
    {
      id: 'group',
      title: 'Group',
      width: 120,
    },
    {
      id: 'status',
      title: 'Status',
      width: 100,
    },
    {
      id: 'health',
      title: 'Health',
      width: 150,
    },
    {
      id: 'alertsLast7days',
      title: 'Alerts last 7 days',
      width: 140,
    },
    {
      id: 'eventsLast7days',
      title: 'Events last 7 days',
      width: 140,
    },
    {
      id: 'history',
      title: 'History',
      width: 150,
      enableSorting: false,
    },
  ];

  // Add upgrade column if needed
  if (showUpgradeColumn) {
    baseColumns.push({
      id: 'upgrade',
      title: '',
      width: 60,
      enableSorting: false,
      enableFiltering: false,
      enableResize: false,
      pinned: 'right',
    });
  }

  baseColumns.push({
    id: 'actions',
    title: '',
    width: 60,
    enableSorting: false,
    enableFiltering: false,
    enableResize: false,
    pinned: 'right',
  });

  const withTooltipDisabled =
    tableColumnDefinitionsWithTooltipDisabled(baseColumns);
  return tableColumnDefinitionsWithFilteringDisabled(withTooltipDisabled);
};

interface CurrentDataSourcesTableProps {
  dataSources: DatasourceSummary[];
  isLoading: boolean;
}

interface DatasourceSummaryWithLoading extends DatasourceSummary {
  isLoadingMetricsAndAlerts: boolean;
}

const transformCurrentDataSourceData = (
  dataSource: DatasourceSummaryWithLoading,
): CurrentDataSourceTableData => {
  const group = dataSource.sourceType || 'Custom';

  return {
    id: dataSource.name,
    name: dataSource.name,
    description: dataSource.description,
    author: dataSource.author,
    group,
    enabled: dataSource.enabled ?? false,
    status: dataSource.enabled ?? false,
    health: dataSource.alerts?.currentlyOpen ? 'error' : 'healthy',
    eventsLast24hrs: dataSource.metrics?.eventsLast24hours,
    eventsLast7days: dataSource.metrics?.eventsLast7days ?? 0,
    alertsLast7days: dataSource.alerts?.alertsLast7days ?? 0,
    iconURL: dataSource.iconURL,
    sparklineData: dataSource.metrics?.eventsSparkLine,
    originalDataSource: dataSource,
    isLoadingMetricsAndAlerts: dataSource.isLoadingMetricsAndAlerts,
  };
};

function CurrentDataSourceTableContent({
  dataSources,
  isLoading,
}: CurrentDataSourcesTableProps) {
  const navigate = useNavigate();

  const {
    data: summary,
    isLoading: isLoadingSummary,
    isFetching: isFetchingSummary,
  } = useGetDatasourcesSummary({
    alerts: true,
    metrics: true,
  });

  // Merge the summary data with the dataSources
  const summaryWithAlertsAndMetrics = useMemo(() => {
    if (!summary?.dataSources || isLoadingSummary || isFetchingSummary)
      return dataSources.map(
        (dataSource) =>
          ({
            ...dataSource,
            isLoadingMetricsAndAlerts: true,
          }) as DatasourceSummaryWithLoading,
      );

    return dataSources.map((dataSource) => {
      const summaryData = summary.dataSources?.find(
        (ds) => ds.name === dataSource.name,
      );
      if (summaryData) {
        return {
          ...dataSource,
          alerts: summaryData.alerts || dataSource.alerts,
          metrics: summaryData.metrics || dataSource.metrics,
          isLoadingMetricsAndAlerts: false,
        } as DatasourceSummaryWithLoading;
      }

      return {
        ...dataSource,
        isLoadingMetricsAndAlerts: false,
      } as DatasourceSummaryWithLoading;
    });
  }, [dataSources, isLoadingSummary, isFetchingSummary, summary?.dataSources]);

  const items = useMemo(
    () => summaryWithAlertsAndMetrics.map(transformCurrentDataSourceData),
    [summaryWithAlertsAndMetrics],
  );

  // Sorts the items for specific columns
  const { sortedItems, setSortBy, sortKey, sortDirection } =
    useTableColumnSorting(items);

  // Creates & manages the columns
  const { columns, columnVisibility, columnWidths } = useTableColumns({
    sortKey,
    sortDirection,
    setSortBy,
  });

  const customBodyCells: VirtualizedCustomBodyCells<CurrentDataSourceTableData> =
    useMemo(
      () => ({
        name: ({ item }) => (
          <div className="flex w-full items-center gap-2">
            <div className="min-w-0">
              <div className="truncate font-medium">{item.name}</div>
              {item.description && (
                <div className="truncate text-xs text-gray-500">
                  {item.description}
                </div>
              )}
            </div>
          </div>
        ),
        image: ({ item }) => (
          <div className="flex w-full items-center gap-2">
            <DataSourceIcon
              iconURL={item.iconURL}
              name={item.name}
              className="h-10 w-10 flex-shrink-0"
            />
          </div>
        ),
        author: ({ item }) => (
          <span className="text-sm">{item.author || '-'}</span>
        ),
        group: ({ item }) => <span className="text-sm">{item.group}</span>,
        status: ({ item }) => <DataSourceEnabledBadge enabled={item.enabled} />,
        health: ({ item }) => (
          <DatasourceStatus
            summaryWithAlertsAndMetrics={item.originalDataSource}
            isLoadingMetricsAndAlerts={item.isLoadingMetricsAndAlerts}
          />
        ),
        alertsLast7days: ({ item }) => (
          <DataSourceAlertCount
            summaryWithAlertsAndMetrics={item.originalDataSource}
            isLoading={item.isLoadingMetricsAndAlerts}
            period="last7days"
            className="text-right"
          />
        ),
        eventsLast7days: ({ item }) => (
          <DataSourceEventCount
            summaryWithAlertsAndMetrics={item.originalDataSource}
            isLoading={item.isLoadingMetricsAndAlerts}
            period="last7days"
            className="text-right"
          />
        ),
        history: ({ item }) => (
          <DataSourceSparkline
            summaryWithAlertsAndMetrics={item.originalDataSource}
            isLoading={item.isLoadingMetricsAndAlerts}
            className="h-8 w-20"
          />
        ),
        upgrade: ({ item }) => (
          <DataSourceUpgrade
            summaryWithAlertsAndMetrics={item.originalDataSource}
            iconOnly
            data-testid={`data-source-upgrade-${item.id}`}
          />
        ),
        actions: ({ item }) => (
          <DataSourceMenu
            dataSource={item.originalDataSource}
            data-testid={`data-source-menu-${item.id}`}
          />
        ),
      }),
      [],
    );

  const { fixedHeaderContent, itemContent } =
    useVirtuosoTableRows<CurrentDataSourceTableData>({
      columns,
      isLoading: false,
      customBodyCells,
      'data-testid': 'current-data-sources-table',
    });

  if (!dataSources?.length && !isLoading) {
    return (
      <div
        className="m-auto flex h-fit w-full flex-col"
        data-testid="current-datasources-section-empty"
      >
        <div className="flex flex-col items-center justify-center gap-2">
          <Icon
            icon="antimatterhq:custom-datasource"
            width={100}
            className="text-grey-400 dark:text-grey-400"
          />
          <Spacer size="base" />
          <div className="flex flex-col items-center gap-2">
            <Text size="lg" semiBold color="info" className="text-grey-400">
              Add a datasource to get started.
            </Text>
            <Spacer size="base" />
            <Button
              variant="primary"
              onClick={() => navigate('/configure/data-sources/add')}
              data-testid="add-datasource-button"
            >
              <Icon icon="lucide:plus" />
              Add datasource
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Table.VirtualizedPageContainer className="h-[calc(100vh-224px)]">
      <Table.StyleProvider
        virtualized={true}
        pageSize={sortedItems.length}
        columnWidths={columnWidths}
        columnVisibility={columnVisibility}
        data-testid="current-data-sources-table-style-provider"
      >
        <Table.Virtual
          columns={columns}
          data={sortedItems}
          isLoading={isLoading}
          itemContent={itemContent}
          fixedHeaderContent={fixedHeaderContent}
          emptyMessage="No data sources found"
          itemHeight={64}
        />
      </Table.StyleProvider>
    </Table.VirtualizedPageContainer>
  );
}

export function CurrentDataSourcesTable({
  dataSources,
  isLoading,
}: CurrentDataSourcesTableProps) {
  const { enablePresetVersioning } = useFlags<FlagSet>();
  const columnDefinitions = useMemo(
    () => getCurrentDataSourcesTableColumnDefinitions(enablePresetVersioning),
    [enablePresetVersioning],
  );

  return (
    <Table.Provider
      columnDefinitions={columnDefinitions}
      localStorageKeyPrefix="current-data-sources-table"
    >
      <CurrentDataSourceTableContent
        dataSources={dataSources}
        isLoading={isLoading}
      />
    </Table.Provider>
  );
}
